import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI } from '../../services/api';

/**
 * 報表資料檢視器
 * Excel 風格的欄位篩選功能
 */
// 後端單次最多回傳筆數（functions/src/api/vendorReports 的上限）
const MAX_ROWS = 1000;

export default function ReportDataViewer({ report }) {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  // 一次取得整份報表（不分頁），篩選與排序都在畫面上處理
  const { data, isLoading, error } = useQuery({
    queryKey: ['reportData', report.id, 'all'],
    queryFn: () => reportsAPI.getData(report.id, { limit: MAX_ROWS }),
    staleTime: 5 * 60 * 1000,
  });

  const allRows = useMemo(() => data?.data || [], [data]);
  const totalInReport = data?.pagination?.total ?? allRows.length;
  const isTruncated = totalInReport > allRows.length;

  // 計算每個欄位的唯一值（篩選選項）
  const columnUniqueValues = useMemo(() => {
    const result = {};
    report.columns?.forEach(col => {
      const values = new Set(allRows.map(row => row[col]).filter(v => v !== null && v !== undefined && v !== ''));
      result[col] = Array.from(values).sort();
    });
    return result;
  }, [allRows, report.columns]);

  // 套用篩選與排序（規則與後端相同：數字以數值排序，其餘依文字）
  const rows = useMemo(() => {
    let result = allRows;
    Object.entries(columnFilters).forEach(([column, values]) => {
      if (values.length > 0) {
        result = result.filter(row => values.includes(String(row[column])));
      }
    });
    if (sortColumn) {
      const direction = sortOrder === 'desc' ? -1 : 1;
      result = [...result].sort((a, b) => {
        const aVal = a[sortColumn] || '';
        const bVal = b[sortColumn] || '';
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return (aNum - bNum) * direction;
        }
        return String(aVal).localeCompare(String(bVal)) * direction;
      });
    }
    return result;
  }, [allRows, columnFilters, sortColumn, sortOrder]);

  // 處理排序
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  // 處理篩選變更
  const handleFilterChange = (column, value, checked) => {
    setColumnFilters(prev => {
      const current = prev[column] || [];
      let updated;
      if (checked) {
        updated = [...current, value];
      } else {
        updated = current.filter(v => v !== value);
      }
      if (updated.length === 0) {
        const { [column]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [column]: updated };
    });
  };

  // 清除欄位篩選
  const clearColumnFilter = (column) => {
    setColumnFilters(prev => {
      const { [column]: _, ...rest } = prev;
      return rest;
    });
  };

  // 清除所有篩選
  const clearAllFilters = () => {
    setColumnFilters({});
  };

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-4 text-red-600">
        載入失敗: {error.message}
      </div>
    );
  }

  const columns = report.columns || [];
  const hasActiveFilters = Object.keys(columnFilters).length > 0;

  return (
    // 高度跟著資料筆數，最高到視窗可用高度（扣除導覽列、頁面標題與上下間距）；超出時表格在框內上下、左右捲動
    <div className="bg-white rounded-lg shadow flex flex-col max-h-[calc(100vh-14rem)]">
      {/* 標題列 */}
      <div className="px-4 py-3 border-b flex justify-between items-center flex-shrink-0">
        <div>
          <h2 className="font-semibold text-gray-800">{report.name}</h2>
          <p className="text-sm text-gray-500">
            {hasActiveFilters
              ? `顯示 ${rows.length.toLocaleString()} / ${allRows.length.toLocaleString()} 筆（已篩選）`
              : `共 ${totalInReport.toLocaleString()} 筆資料`}
          </p>
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            清除所有篩選
          </button>
        )}
      </div>

      {/* 超過單次顯示上限時的警語 */}
      {isTruncated && (
        <div className="px-4 py-2 bg-yellow-50 border-b text-sm text-yellow-800 flex-shrink-0">
          ⚠ 此報表共 {totalInReport.toLocaleString()} 筆，畫面僅顯示前 {allRows.length.toLocaleString()} 筆，篩選與排序也只針對這些資料。
        </div>
      )}

      {/* 目前篩選條件 */}
      {hasActiveFilters && (
        <div className="px-4 py-2 bg-blue-50 border-b flex flex-wrap gap-2 flex-shrink-0">
          {Object.entries(columnFilters).map(([column, values]) => (
            <span
              key={column}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
            >
              <span className="font-medium">{column}:</span>
              <span>{values.length > 2 ? `${values.length} 個選項` : values.join(', ')}</span>
              <button
                onClick={() => clearColumnFilter(column)}
                className="ml-1 hover:text-blue-600"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* 資料表格：資料超出時出現上下、左右捲軸 */}
      <div className="flex-1 min-h-0 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-20">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-4 py-3 text-left font-medium text-gray-700 whitespace-nowrap relative group"
                >
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleSort(column)}
                      className="hover:text-blue-600 flex items-center gap-1"
                    >
                      {column}
                      {sortColumn === column && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d={sortOrder === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                          />
                        </svg>
                      )}
                    </button>
                    {/* 篩選按鈕 */}
                    <button
                      onClick={() => setActiveFilterColumn(activeFilterColumn === column ? null : column)}
                      className={`p-1 rounded hover:bg-gray-200 ${
                        columnFilters[column]?.length > 0 ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </button>
                  </div>

                  {/* 篩選下拉選單 */}
                  {activeFilterColumn === column && (
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-10">
                      <div className="p-2 border-b">
                        <div className="text-xs text-gray-500 mb-1">篩選「{column}」</div>
                        <button
                          onClick={() => clearColumnFilter(column)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          清除篩選
                        </button>
                      </div>
                      <div className="max-h-60 overflow-y-auto p-2">
                        {columnUniqueValues[column]?.slice(0, 100).map((value) => (
                          <label
                            key={value}
                            className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={columnFilters[column]?.includes(value) || false}
                              onChange={(e) => handleFilterChange(column, value, e.target.checked)}
                              className="rounded"
                            />
                            <span className="text-sm truncate">{value}</span>
                          </label>
                        ))}
                        {columnUniqueValues[column]?.length > 100 && (
                          <div className="text-xs text-gray-400 p-1">
                            ...還有 {columnUniqueValues[column].length - 100} 個選項
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  載入中...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                  無資料
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td key={column} className="px-4 py-2 whitespace-nowrap">
                      {row[column] ?? '-'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 點擊外部關閉篩選選單 */}
      {activeFilterColumn && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setActiveFilterColumn(null)}
        />
      )}
    </div>
  );
}
